import React, { useEffect } from "react";
import Alert from "components/Alert";
import useFeatureFlag from "hooks/useFeatureFlag";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";
import GraphicObject from "../components/GraphicObject";
import { COEStore } from "../ComponentEditorStore";
import clsx from "clsx";
import Button from "components/Button";
import { IconRotateClockwise } from "@tabler/icons-react";
import { BPEStore } from "features/BuildingProductEditor/ProductEditorStore";
import { GraphicOrientation } from "../../../api-client";

const ComponentGraphic = () => {
  const { isFeatureVisible } = useFeatureFlag();
  const { t } = useTranslation();
  const graphicData = COEStore.componentInsights;

  useEffect(() => {
    BPEStore.loadPatterns();
  }, []);

  if (!isFeatureVisible("product_graphic")) return <></>;

  const hasGraphics = COEStore.data.childrenList?.find(
    (item) =>
      ["M2", "M3"].includes(item.reference_unit) &&
      item.thickness &&
      item.unit_conversion
  );

  const hasAnyComponentChild = COEStore.data.childrenList?.some(
    (item) => item.type === "COMPONENT"
  );

  const hasNotValidProductUnit = () => {
    return COEStore.data.childrenList?.some(
      (item) => !["M2", "M3"].includes(item.reference_unit)
    );
  };

  const handleChangeRotation = async () => {
    const desired =
      COEStore.graphicOrientation === GraphicOrientation.Horizontal
        ? GraphicOrientation.Vertical
        : GraphicOrientation.Horizontal;
    await COEStore.fetchComponentInsights(desired);
  };

  let alertMessage: string | null = null;

  if (COEStore.data.reference_unit.id !== "M2") {
    alertMessage = t("components.graphicRefUnitAlert");
  } else if (hasAnyComponentChild) {
    alertMessage = t("components.graphicGenerateErrorAlert");
  } else if (!hasGraphics) {
    alertMessage = t("components.graphicGenerateAlert");
  }

  React.useEffect(() => {
    // clear diagram when it has an error
    alertMessage && COEStore.setDiagramsToUpload(undefined);
  }, [alertMessage]);

  return (
    <div className="py-6 border-b border-gray-200 xl:border-b-0 xl:border-t">
      <div className="flex justify-between mb-3">
        <div className="text-base font-semibold flex items-center">
          {t("components.layerStructure")}
        </div>
        <Button
          type="gray"
          width="fit-content"
          size="small"
          leadingIcon={<IconRotateClockwise size={16} stroke={3} />}
          onClick={handleChangeRotation}
        >
          {t("components.rotateStructure")}
        </Button>
      </div>
      {alertMessage ? (
        <Alert
          type="info"
          description={alertMessage}
          iconClassName="flex items-center"
        />
      ) : (
        hasGraphics &&
        graphicData && (
          <div className="bg-gray-50 rounded-md flex flex-col items-center justify-between py-2 relative">
            <div className="flex flex-col items-center justify-center">
              {hasGraphics && (
                <GraphicObject
                  info={graphicData}
                  patternImages={BPEStore.patternImages}
                  orientation={COEStore.graphicOrientation}
                  countOfProducts={COEStore.data.childrenList?.length || 0}
                />
              )}
            </div>
            {hasNotValidProductUnit() && (
              <Alert
                type="info"
                description={t("components.graphicAlert")}
                className={clsx("bg-transparent border-none py-0 px-4", {
                  "mt-3": hasGraphics,
                })}
                iconClassName="flex items-center"
              />
            )}
          </div>
        )
      )}
    </div>
  );
};

export default observer(ComponentGraphic);
